import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { getBlogById } from "../../services/servicesblogs";
import "./BlogDetails.css";

const VIDEO_FILE_PATTERN = /\.(mp4|webm|ogg|mov|m4v|avi)(\?.*)?$/i;

// Formats different date shapes returned by the API into a readable value.
const getDateValue = (value) => {
  const raw = value && typeof value === "object" ? value.$date ?? value.date ?? value.iso ?? null : value;
  if (!raw) return "--";
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? "--" : date.toLocaleDateString("en-GB");
};

const inferMediaType = (value = "") =>
  VIDEO_FILE_PATTERN.test(value) ? "video" : "image";

// Always returns featured media as an array for simpler rendering.
const toMediaList = (blog) => {
  const raw = blog?.featured_image ?? blog?.featuredImage ?? [];
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];

  return list
    .map((item) => {
      if (typeof item === "string") {
        return {
          src: item,
          type: inferMediaType(item),
        };
      }

      const src = item?.url ?? item?.src ?? item?.secure_url ?? "";
      if (!src) return null;

      return {
        src,
        type: item?.type || inferMediaType(src),
      };
    })
    .filter(Boolean);
};

// Read-only page for viewing a single blog post.
export default function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [blog, setBlog] = useState(location.state?.blog || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the latest blog details using the route id.
    const loadBlog = async () => {
      if (!id) return;
      try {
        const response = await getBlogById(id);
        const payload = response?.data ?? response;
        const resolvedBlog = payload?.blog ?? payload?.data ?? payload;
        setBlog((prev) => ({ ...(prev || {}), ...(resolvedBlog || {}) }));
      } catch (error) {
        console.error("Error fetching blog details:", error);
        setBlog((prev) => prev || null);
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [id]);

  if (!loading && !blog) {
    return <Navigate to="/ContentManager/ContentManagerLayout/blogposts" replace />;
  }

  if (loading) {
    return <p className="loading">Loading blog details...</p>;
  }

  const mediaItems = toMediaList(blog);

  return (
    <div className="blog-details-page">
      <button type="button" className="blog-details-back" onClick={() => navigate(-1)}>
        <span aria-hidden="true">←</span>
      </button>
      <h1 className="blog-details-title">Blog Details</h1>

      <section className="blog-details-card">
        <div className="blog-details-block">
          <span className="blog-details-label">Title</span>
          <h2>{blog.title || "--"}</h2>
        </div>

        <div className="blog-details-block">
          <span className="blog-details-label">Slug</span>
          <p>/{blog.slug || "--"}</p>
        </div>

        <div className="blog-details-grid">
          <div className="blog-details-block">
            <span className="blog-details-label">Status</span>
            <span className={`blog-details-badge ${String(blog.status).toUpperCase() === "PUBLISHED" ? "published" : "draft"}`}>
              {String(blog.status || "DRAFT").toLowerCase()}
            </span>
          </div>

          <div className="blog-details-block">
            <span className="blog-details-label">Updated</span>
            <p>{getDateValue(blog.updatedAt ?? blog.updated_at ?? blog.createdAt)}</p>
          </div>
        </div>

        <div className="blog-details-block">
          <span className="blog-details-label">Content</span>
          <p className="blog-details-copy">{blog.content || "--"}</p>
        </div>

        <div className="blog-details-block">
          <span className="blog-details-label">Meta Title</span>
          <p>{blog.meta_title || blog.metaTitle || "--"}</p>
        </div>

        <div className="blog-details-block">
          <span className="blog-details-label">Meta Description</span>
          <p className="blog-details-copy">{blog.meta_description || blog.metaDescription || "--"}</p>
        </div>

        <div className="blog-details-block">
          <span className="blog-details-label">Featured Image</span>
          {mediaItems.length ? (
            <div className="blog-details-images">
              {mediaItems.map((item) => (
                item.type === "video" ? (
                  <video key={item.src} controls preload="metadata">
                    <source src={item.src} />
                  </video>
                ) : (
                  <img
                    key={item.src}
                    src={item.src}
                    alt={blog.title || "Blog featured"}
                  />
                )
              ))}
            </div>
          ) : (
            <p>--</p>
          )}
        </div>
      </section>
    </div>
  );
}
